<template>
	<div class="home-dashboard">
		<!-- Snackbar for notifications -->
		<v-snackbar v-model="snackbar" :timeout="4000" top right :color="color" elevation="8">
			<div class="d-flex align-center">
				<v-icon left small>{{ color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
				<span>{{ message }}</span>
			</div>
			<template v-slot:action="{ attrs }">
				<v-btn icon small v-bind="attrs" @click="snackbar = false">
					<v-icon small>mdi-close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>

		<!-- Hero Carousel Section -->
		<section class="hero-section" v-if="allAds && allAds.length > 0">
			<v-carousel
				cycle
				interval="8000"
				height="320"
				hide-delimiter-background
				show-arrows-on-hover
				class="hero-carousel"
			>
				<v-carousel-item v-for="(ad, i) in allAds" :key="i">
					<v-sheet :color="ad.background_color" height="100%" class="hero-slide">
						<v-container fluid class="fill-height pa-0">
							<v-row class="fill-height ma-0" align="center">
								<v-col cols="12" md="5" class="d-flex justify-center align-center">
									<v-img
										contain
										max-height="280"
										max-width="350"
										class="hero-image"
										:src="path + '/storage/ad_pictures/' + ad.image"
									/>
								</v-col>
								<v-col cols="12" md="7" class="hero-content">
									<h1 :class="'hero-title ' + ad.text_color + '--text'">
										{{ ad.title }}
									</h1>
									<p :class="'hero-tagline ' + ad.text_color + '--text'">
										{{ ad.tagline }}
									</p>
									<div
										:class="'hero-description ' + ad.text_color + '--text'"
										v-html="ad.description"
									></div>
								</v-col>
							</v-row>
						</v-container>
					</v-sheet>
				</v-carousel-item>
			</v-carousel>
		</section>

		<!-- Main Dashboard Content -->
		<v-container fluid class="dashboard-content pa-4 pa-md-6">
			<!-- No Student Assigned Alert -->
			<v-row v-if="allStudentLevels.length === 0 && !studentLevelLoader">
				<v-col cols="12">
					<v-card class="empty-state-card" elevation="0">
						<v-card-text class="text-center py-12">
							<v-icon size="80" color="grey lighten-1" class="mb-4">mdi-account-school</v-icon>
							<h2 class="text-h5 grey--text text--darken-1 mb-2">No Students Assigned</h2>
							<p class="text-body-1 grey--text mb-6">
								No student has been assigned to your account yet, or they haven't been assigned a class.
							</p>
							<v-btn color="primary" outlined>
								<v-icon left>mdi-help-circle</v-icon>
								Contact Support
							</v-btn>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>

			<!-- Loading State -->
			<v-row v-else-if="studentLevelLoader">
				<v-col cols="12" class="text-center py-12">
					<v-progress-circular
						indeterminate
						color="primary"
						size="64"
						width="4"
					></v-progress-circular>
					<p class="mt-4 text-body-1 grey--text">Loading your dashboard...</p>
				</v-col>
			</v-row>

			<!-- Dashboard with Student Data -->
			<template v-else-if="allStudentLevels.length > 0">
				<!-- Student Selector Card -->
				<v-row class="mb-4">
					<v-col cols="12">
						<v-card class="student-selector-card" elevation="2">
							<v-card-text class="pa-0">
								<v-row no-gutters align="center">
									<!-- Student Chips/Tabs -->
									<v-col cols="12" md="8" class="pa-3">
										<div class="d-flex align-center flex-wrap">
											<span class="text-subtitle-2 grey--text mr-3">Select Student:</span>
											<v-chip-group
												v-model="activeStudent"
												mandatory
												active-class="primary"
											>
												<v-chip
													v-for="(studentLevel, index) in allStudentLevels"
													:key="index"
													class="student-chip"
													:class="{ 'white--text': activeStudent === index }"
													:outlined="activeStudent !== index"
													label
													@click="assignStudent(index)"
												>
													<v-avatar left size="28" v-if="studentLevel.student.prof_pic">
														<v-img :src="path + '/storage/part_pictures/' + studentLevel.student.prof_pic" />
													</v-avatar>
													<v-avatar left size="28" :color="activeStudent === index ? 'white' : 'primary'" v-else>
														<span :class="activeStudent === index ? 'primary--text' : 'white--text'" class="text-caption">
															{{ getInitials(studentLevel.student) }}
														</span>
													</v-avatar>
													{{ studentLevel.student.first_name }} {{ studentLevel.student.last_name }}
												</v-chip>
											</v-chip-group>
										</div>
									</v-col>
									<!-- Quick Stats -->
									<v-col cols="12" md="4" class="pa-3 border-left-md">
										<div class="d-flex justify-space-around text-center">
											<div class="stat-item">
												<div class="text-h5 primary--text font-weight-bold">
													{{ currentStudentParts.length }}
												</div>
												<div class="text-caption grey--text">Total Items</div>
											</div>
											<v-divider vertical class="mx-2"></v-divider>
											<div class="stat-item">
												<div class="text-h5 success--text font-weight-bold">
													{{ purchasedItemsCount }}
												</div>
												<div class="text-caption grey--text">Purchased</div>
											</div>
											<v-divider vertical class="mx-2"></v-divider>
											<div class="stat-item">
												<div class="text-h5 accent--text font-weight-bold">
													{{ pendingItemsCount }}
												</div>
												<div class="text-caption grey--text">Remaining</div>
											</div>
										</div>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>

				<!-- Main Content Area -->
				<v-row>
					<!-- Sidebar Filters -->
					<v-col cols="12" md="3" lg="2" class="filter-sidebar">
						<!-- Student Info Card -->
						<v-card class="student-info-card mb-4" elevation="1">
							<v-card-text class="text-center pa-4">
								<v-avatar size="100" class="mb-3 elevation-4">
									<v-img
										v-if="currentStudent.prof_pic"
										:src="path + '/storage/part_pictures/' + currentStudent.prof_pic"
									/>
									<v-img v-else src="img/avatar.jpg" />
								</v-avatar>
								<h3 class="text-subtitle-1 font-weight-bold mb-1">
									{{ currentStudent.first_name }} {{ currentStudent.middle_name }}
								</h3>
								<p class="text-body-2 grey--text mb-3">{{ currentStudent.last_name }}</p>
								<v-chip small color="primary" outlined v-if="currentStudentLevel">
									<v-icon left small>mdi-school</v-icon>
									{{ currentStudentLevel.level.name }} - {{ currentStudentLevel.sub_level.name }}
								</v-chip>
							</v-card-text>
						</v-card>

						<!-- Category Filters -->
						<v-card class="filter-card mb-4" elevation="1">
							<v-card-title class="text-subtitle-2 pb-1">
								<v-icon small left color="primary">mdi-filter-variant</v-icon>
								Filter by Category
							</v-card-title>
							<v-card-text class="pt-0">
								<v-list dense class="filter-list category-tree">
									<!-- All Items -->
									<v-list-item
										class="rounded mb-1"
										:class="{ 'primary white--text': !selectedSubCategory }"
										@click="clearCategoryFilter"
									>
										<v-list-item-icon class="mr-2">
											<v-icon small :color="!selectedSubCategory ? 'white' : ''">mdi-view-grid</v-icon>
										</v-list-item-icon>
										<v-list-item-content>
											<v-list-item-title class="text-body-2">All Items</v-list-item-title>
										</v-list-item-content>
										<v-list-item-action>
											<v-chip x-small :color="!selectedSubCategory ? 'white' : ''" :text-color="!selectedSubCategory ? 'primary' : ''">
												{{ currentStudentParts.length }}
											</v-chip>
										</v-list-item-action>
									</v-list-item>

									<!-- Category Groups with Subcategories -->
									<v-list-group
										v-for="category in allCategories"
										:key="category.id"
										no-action
										sub-group
										class="category-group"
									>
										<template v-slot:activator>
											<v-list-item-icon class="mr-2">
												<v-icon small>{{ getCategoryIcon(category.name) }}</v-icon>
											</v-list-item-icon>
											<v-list-item-content>
												<v-list-item-title class="text-body-2">{{ category.name }}</v-list-item-title>
											</v-list-item-content>
										</template>

										<!-- Subcategories -->
										<v-list-item
											v-for="subCategory in category.sub_categories"
											:key="subCategory.id"
											class="pl-8 rounded mb-1 subcategory-item"
											:class="{ 'primary white--text': selectedSubCategory && selectedSubCategory.id === subCategory.id }"
											@click="selectSubCategory(subCategory)"
										>
											<v-list-item-content>
												<v-list-item-title class="text-body-2">{{ subCategory.name }}</v-list-item-title>
											</v-list-item-content>
											<v-list-item-action v-if="selectedSubCategory && selectedSubCategory.id === subCategory.id">
												<v-icon x-small color="white">mdi-check</v-icon>
											</v-list-item-action>
										</v-list-item>
									</v-list-group>
								</v-list>
							</v-card-text>
						</v-card>

						<!-- Learning Area Filters (filtered by selected student) -->
						<v-card class="filter-card mb-4" elevation="1" v-if="studentLearningAreas && studentLearningAreas.length > 0">
							<v-card-title class="text-subtitle-2 pb-1">
								<v-icon small left color="secondary">mdi-book-open-variant</v-icon>
								Learning Areas
							</v-card-title>
							<v-card-text class="pt-0">
								<v-list dense class="filter-list">
									<!-- All Learning Areas -->
									<v-list-item
										class="rounded mb-1"
										:class="{ 'secondary white--text': !selectedLearningArea }"
										@click="clearLearningAreaFilter"
									>
										<v-list-item-icon class="mr-2">
											<v-icon small :color="!selectedLearningArea ? 'white' : 'secondary'">mdi-book-open-variant</v-icon>
										</v-list-item-icon>
										<v-list-item-content>
											<v-list-item-title class="text-body-2">All Subjects</v-list-item-title>
										</v-list-item-content>
									</v-list-item>

									<v-list-item
										v-for="area in studentLearningAreas"
										:key="area.id"
										class="rounded mb-1"
										:class="{ 'secondary white--text': selectedLearningArea && selectedLearningArea.id === area.id }"
										@click="selectLearningArea(area)"
									>
										<v-list-item-icon class="mr-2">
											<v-icon small :color="selectedLearningArea && selectedLearningArea.id === area.id ? 'white' : 'secondary'">
												mdi-book-open-page-variant
											</v-icon>
										</v-list-item-icon>
										<v-list-item-content>
											<v-list-item-title class="text-body-2">{{ area.name }}</v-list-item-title>
										</v-list-item-content>
										<v-list-item-action v-if="selectedLearningArea && selectedLearningArea.id === area.id">
											<v-icon x-small color="white">mdi-check</v-icon>
										</v-list-item-action>
									</v-list-item>
								</v-list>
							</v-card-text>
						</v-card>

						<!-- Price Range Filter -->
						<v-card class="filter-card" elevation="1">
							<v-card-title class="text-subtitle-2 pb-1">
								<v-icon small left color="accent">mdi-cash</v-icon>
								Price Range
							</v-card-title>
							<v-card-text>
								<v-range-slider
									v-model="priceRange"
									:min="minPrice"
									:max="maxPrice"
									step="100"
									thumb-label
									color="accent"
									track-color="grey lighten-3"
								>
									<template v-slot:thumb-label="{ value }">
										{{ formatPrice(value) }}
									</template>
								</v-range-slider>
								<div class="d-flex justify-space-between text-caption grey--text">
									<span>Kshs {{ formatPrice(priceRange[0]) }}</span>
									<span>Kshs {{ formatPrice(priceRange[1]) }}</span>
								</div>
							</v-card-text>
						</v-card>
					</v-col>

					<!-- Products Grid -->
					<v-col cols="12" md="9" lg="10">
						<!-- Toolbar -->
						<v-card class="toolbar-card mb-4" elevation="1">
							<v-card-text class="pa-3">
								<v-row align="center" no-gutters>
									<v-col cols="12" sm="5" md="4">
										<v-text-field
											v-model="searchQuery"
											dense
											outlined
											hide-details
											placeholder="Search items..."
											prepend-inner-icon="mdi-magnify"
											clearable
											class="search-field"
										></v-text-field>
									</v-col>
									<v-col cols="12" sm="7" md="8" class="d-flex align-center justify-end mt-3 mt-sm-0">
										<v-btn-toggle v-model="viewMode" mandatory dense class="mr-3">
											<v-btn small value="grid">
												<v-icon small>mdi-view-grid</v-icon>
											</v-btn>
											<v-btn small value="list">
												<v-icon small>mdi-view-list</v-icon>
											</v-btn>
										</v-btn-toggle>
										<v-menu offset-y>
											<template v-slot:activator="{ on, attrs }">
												<v-btn outlined small v-bind="attrs" v-on="on" class="text-none">
													<v-icon left small>mdi-sort</v-icon>
													{{ sortOptions.find(s => s.value === sortBy)?.text || 'Sort' }}
													<v-icon right small>mdi-chevron-down</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item
													v-for="option in sortOptions"
													:key="option.value"
													@click="sortBy = option.value"
												>
													<v-list-item-title>{{ option.text }}</v-list-item-title>
												</v-list-item>
											</v-list>
										</v-menu>
										<v-chip small class="ml-3" color="grey lighten-3">
											{{ filteredParts.length }} items
										</v-chip>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>

						<!-- Active Filters -->
						<div class="active-filters mb-3" v-if="hasActiveFilters">
							<v-chip
								v-if="selectedSubCategory"
								small
								close
								color="primary"
								outlined
								class="mr-2"
								@click:close="clearCategoryFilter"
							>
								Category: {{ selectedSubCategory.name }}
							</v-chip>
							<v-chip
								v-if="selectedLearningArea"
								small
								close
								color="secondary"
								outlined
								class="mr-2"
								@click:close="clearLearningAreaFilter"
							>
								Subject: {{ selectedLearningArea.name }}
							</v-chip>
							<v-chip
								v-if="searchQuery"
								small
								close
								color="grey"
								outlined
								class="mr-2"
								@click:close="searchQuery = ''"
							>
								Search: {{ searchQuery }}
							</v-chip>
							<v-btn x-small text color="error" @click="clearAllFilters" class="text-none">
								Clear All
							</v-btn>
						</div>

						<!-- Loading indicator for category filter -->
						<div v-if="categoryFilterLoading" class="text-center py-4">
							<v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
							<p class="text-caption grey--text mt-2">Loading products...</p>
						</div>

						<!-- Empty State -->
						<v-card v-if="filteredParts.length === 0 && !categoryFilterLoading" class="empty-products-card" elevation="0">
							<v-card-text class="text-center py-12">
								<v-icon size="64" color="grey lighten-1" class="mb-4">mdi-package-variant</v-icon>
								<h3 class="text-h6 grey--text text--darken-1 mb-2">No Items Found</h3>
								<p class="text-body-2 grey--text">
									{{ hasActiveFilters ? 'Try adjusting your filters or search terms' : 'No stock items available for this student yet. Please check back later.' }}
								</p>
								<v-btn v-if="hasActiveFilters" color="primary" outlined small @click="clearAllFilters" class="mt-4">
									Clear Filters
								</v-btn>
							</v-card-text>
						</v-card>

						<!-- Grid View -->
						<v-row v-else-if="viewMode === 'grid' && !categoryFilterLoading" class="products-grid">
							<v-col
								v-for="part in filteredParts"
								:key="part.id"
								cols="12"
								sm="6"
								md="4"
								lg="3"
							>
								<v-card class="product-card" elevation="2" :class="{ 'purchased-card': part.purchased_quantity > 0 }">
									<!-- Product Image -->
									<div class="product-image-wrapper">
										<v-img
											:src="part.image ? path + '/storage/part_pictures/' + part.image : 'img/book-silhouette-vector-14.png'"
											height="180"
											contain
											class="product-image"
											:class="{ 'placeholder-image': !part.image }"
										>
											<!-- Badges -->
											<div class="product-badges">
												<v-chip
													v-if="part.purchased_quantity > 0"
													small
													color="success"
													class="badge purchased-badge"
												>
													<v-icon left x-small>mdi-check</v-icon>
													Purchased ({{ part.purchased_quantity }})
												</v-chip>
												<v-chip
													v-else-if="part.quantity === 0"
													small
													color="error"
													class="badge"
												>
													Out of Stock
												</v-chip>
											</div>

											<!-- Wishlist Button -->
											<v-btn
												icon
												small
												class="wishlist-btn"
												:loading="wishlistLoading && partIndex === part.id"
												@click.stop="toggleWishlist(part)"
											>
												<v-icon :color="part.favourite ? 'red' : 'grey lighten-1'" small>
													{{ part.favourite ? 'mdi-heart' : 'mdi-heart-outline' }}
												</v-icon>
											</v-btn>
										</v-img>
									</div>

									<!-- Product Info -->
									<v-card-text class="product-info pa-3">
										<!-- Category Tags -->
										<div class="category-tags mb-2" v-if="getPartCategories(part).length > 0">
											<v-chip
												v-for="cat in getPartCategories(part).slice(0, 2)"
												:key="cat"
												x-small
												outlined
												color="primary"
												class="mr-1"
											>
												{{ cat }}
											</v-chip>
										</div>

										<!-- Product Name -->
										<router-link
											:to="'/product/' + part.slug"
											class="product-name-link"
										>
											<h3 class="product-name">
												<v-clamp autoresize :max-lines="2">{{ part.name }}</v-clamp>
											</h3>
										</router-link>

										<!-- Learning Area -->
										<p class="learning-area text-caption grey--text mb-2" v-if="getPartLearningArea(part)">
											<v-icon x-small class="mr-1">mdi-book-open-variant</v-icon>
											{{ getPartLearningArea(part) }}
										</p>

										<!-- Price -->
										<div class="product-price">
											<span class="price-label">Kshs</span>
											<span class="price-value">{{ formatPrice(part.price) }}</span>
										</div>
									</v-card-text>

									<!-- Actions -->
									<v-divider></v-divider>
									<v-card-actions class="pa-3">
										<v-btn
											block
											:color="part.quantity > 0 ? 'primary' : 'grey'"
											:disabled="part.quantity === 0"
											:loading="cartLoading && partIndex === part.id"
											:outlined="part.purchased_quantity > 0"
											@click="addToCart(part)"
											class="add-to-cart-btn text-none"
										>
											<v-icon left small>mdi-cart-plus</v-icon>
											{{ part.purchased_quantity > 0 ? 'Buy More' : 'Add to Cart' }}
										</v-btn>
									</v-card-actions>
								</v-card>
							</v-col>
						</v-row>

						<!-- List View -->
						<v-card v-else-if="!categoryFilterLoading" elevation="1" class="products-list">
							<v-list three-line>
								<template v-for="(part, index) in filteredParts">
									<v-list-item :key="part.id" class="product-list-item">
										<v-list-item-avatar size="80" tile class="mr-4">
											<v-img
												:src="part.image ? path + '/storage/part_pictures/' + part.image : 'img/book-silhouette-vector-14.png'"
												contain
											></v-img>
										</v-list-item-avatar>

										<v-list-item-content>
											<v-list-item-title>
												<router-link :to="'/product/' + part.slug" class="product-name-link">
													{{ part.name }}
												</router-link>
											</v-list-item-title>
											<v-list-item-subtitle class="mt-1">
												<v-chip
													v-for="cat in getPartCategories(part).slice(0, 2)"
													:key="cat"
													x-small
													outlined
													color="primary"
													class="mr-1"
												>
													{{ cat }}
												</v-chip>
												<span v-if="getPartLearningArea(part)" class="grey--text">
													{{ getPartLearningArea(part) }}
												</span>
											</v-list-item-subtitle>
											<v-list-item-subtitle class="mt-2">
												<span class="primary--text font-weight-bold text-body-1">
													Kshs {{ formatPrice(part.price) }}
												</span>
												<v-chip
													v-if="part.purchased_quantity > 0"
													x-small
													color="success"
													class="ml-2"
												>
													Purchased ({{ part.purchased_quantity }})
												</v-chip>
												<v-chip
													v-else-if="part.quantity === 0"
													x-small
													color="error"
													class="ml-2"
												>
													Out of Stock
												</v-chip>
											</v-list-item-subtitle>
										</v-list-item-content>

										<v-list-item-action class="flex-row align-center">
											<v-btn
												icon
												class="mr-2"
												:loading="wishlistLoading && partIndex === part.id"
												@click="toggleWishlist(part)"
											>
												<v-icon :color="part.favourite ? 'red' : 'grey'">
													{{ part.favourite ? 'mdi-heart' : 'mdi-heart-outline' }}
												</v-icon>
											</v-btn>
											<v-btn
												color="primary"
												:disabled="part.quantity === 0"
												:loading="cartLoading && partIndex === part.id"
												:outlined="part.purchased_quantity > 0"
												@click="addToCart(part)"
												class="text-none"
											>
												<v-icon left small>mdi-cart-plus</v-icon>
												{{ part.purchased_quantity > 0 ? 'Buy More' : 'Add to Cart' }}
											</v-btn>
										</v-list-item-action>
									</v-list-item>
									<v-divider v-if="index < filteredParts.length - 1" :key="'divider-' + part.id"></v-divider>
								</template>
							</v-list>
						</v-card>
					</v-col>
				</v-row>
			</template>
		</v-container>

		<!-- Mobile Filter Drawer -->
		<v-navigation-drawer
			v-model="mobileFilterDrawer"
			temporary
			right
			width="300"
			class="mobile-filter-drawer"
		>
			<v-toolbar flat color="primary" dark>
				<v-toolbar-title>Filters</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-btn icon @click="mobileFilterDrawer = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<!-- Mobile filter content would go here - mirrors sidebar filters -->
		</v-navigation-drawer>

		<!-- Mobile Filter FAB -->
		<v-btn
			fab
			fixed
			bottom
			right
			color="primary"
			class="mobile-filter-fab d-md-none"
			@click="mobileFilterDrawer = true"
		>
			<v-icon>mdi-filter-variant</v-icon>
		</v-btn>
	</div>
</template>

<style scoped>
.home-dashboard {
	min-height: 100vh;
	background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

/* Hero Section */
.hero-section {
	margin-bottom: 0;
}

.hero-carousel {
	border-radius: 0;
}

.hero-carousel :deep(.v-carousel__controls) {
	background: transparent;
}

.hero-slide {
	overflow: hidden;
}

.hero-image {
	transition: transform 0.3s ease;
}

.hero-content {
	padding: 24px 48px;
}

.hero-title {
	font-size: 2.5rem;
	font-weight: 800;
	line-height: 1.2;
	margin-bottom: 8px;
}

.hero-tagline {
	font-size: 1.25rem;
	font-weight: 500;
	margin-bottom: 16px;
	opacity: 0.9;
}

.hero-description {
	font-size: 1rem;
	line-height: 1.6;
	max-width: 600px;
}

/* Dashboard Content */
.dashboard-content {
	max-width: 1600px;
	margin: 0 auto;
}

/* Student Selector */
.student-selector-card {
	border-radius: 12px;
	overflow: hidden;
}

.student-chip {
	transition: all 0.2s ease;
}

.student-chip:hover {
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-item {
	padding: 8px 16px;
}

.border-left-md {
	border-left: 1px solid #e0e0e0;
}

/* Filter Sidebar */
.filter-sidebar {
	position: sticky;
	top: 80px;
	height: fit-content;
}

.student-info-card {
	border-radius: 12px;
	overflow: hidden;
}

.filter-card {
	border-radius: 12px;
}

.filter-list {
	max-height: 350px;
	overflow-y: auto;
}

.filter-list :deep(.v-list-item) {
	min-height: 36px;
	padding: 0 8px;
}

.category-tree :deep(.v-list-group__header) {
	min-height: 36px;
	padding: 0 8px;
}

.category-tree :deep(.v-list-group__items) {
	background: rgba(0, 0, 0, 0.02);
}

.subcategory-item {
	transition: background-color 0.2s ease;
}

.subcategory-item:hover {
	background-color: rgba(40, 22, 111, 0.08);
}

/* Toolbar */
.toolbar-card {
	border-radius: 12px;
}

.search-field :deep(.v-input__slot) {
	border-radius: 8px;
}

/* Product Cards */
.products-grid {
	margin: -8px;
}

.products-grid > .col {
	padding: 8px;
}

.product-card {
	border-radius: 16px;
	overflow: hidden;
	transition: all 0.3s ease;
	height: 100%;
	display: flex;
	flex-direction: column;
}

.product-card:hover {
	transform: translateY(-4px);
	box-shadow: 0 12px 40px rgba(40, 22, 111, 0.15);
}

.product-card.purchased-card {
	border: 2px solid #4caf50;
}

.product-image-wrapper {
	position: relative;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.product-image {
	transition: transform 0.3s ease;
}

.product-card:hover .product-image {
	transform: scale(1.05);
}

.placeholder-image {
	opacity: 0.5;
}

.product-badges {
	position: absolute;
	top: 8px;
	left: 8px;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.badge {
	font-size: 10px;
	height: 24px;
}

.wishlist-btn {
	position: absolute;
	top: 8px;
	right: 8px;
	background: white;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.wishlist-btn:hover {
	transform: scale(1.1);
}

.product-info {
	flex-grow: 1;
	display: flex;
	flex-direction: column;
}

.category-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}

.product-name-link {
	text-decoration: none;
	color: inherit;
}

.product-name-link:hover .product-name {
	color: #28166F;
}

.product-name {
	font-size: 0.95rem;
	font-weight: 600;
	line-height: 1.4;
	color: #333;
	margin-bottom: 4px;
	min-height: 2.8em;
	transition: color 0.2s ease;
}

.learning-area {
	display: flex;
	align-items: center;
}

.product-price {
	margin-top: auto;
}

.price-label {
	font-size: 0.75rem;
	color: #666;
	margin-right: 4px;
}

.price-value {
	font-size: 1.25rem;
	font-weight: 700;
	color: #28166F;
}

.add-to-cart-btn {
	border-radius: 8px;
	font-weight: 600;
}

/* List View */
.products-list {
	border-radius: 12px;
}

.product-list-item {
	padding: 16px;
	transition: background-color 0.2s ease;
}

.product-list-item:hover {
	background-color: #f5f5f5;
}

/* Empty States */
.empty-state-card,
.empty-products-card {
	border-radius: 16px;
	border: 2px dashed #e0e0e0;
	background: transparent;
}

/* Mobile Filter */
.mobile-filter-fab {
	margin-bottom: 16px;
	margin-right: 16px;
}

.mobile-filter-drawer {
	z-index: 1000;
}

/* Responsive */
@media (max-width: 960px) {
	.hero-title {
		font-size: 1.75rem;
	}

	.hero-content {
		padding: 16px 24px;
		text-align: center;
	}

	.filter-sidebar {
		display: none;
	}

	.border-left-md {
		border-left: none;
		border-top: 1px solid #e0e0e0;
	}
}

@media (max-width: 600px) {
	.hero-section :deep(.v-carousel) {
		height: 250px !important;
	}

	.hero-title {
		font-size: 1.5rem;
	}

	.hero-tagline {
		font-size: 1rem;
	}

	.stat-item {
		padding: 4px 8px;
	}
}

/* Scrollbar */
::-webkit-scrollbar {
	width: 6px;
}

::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 3px;
}

::-webkit-scrollbar-thumb {
	background: #c1c1c1;
	border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
	background: #a1a1a1;
}
</style>

<script>
import VClamp from 'vue-clamp'
import apiCall from '@/utils/api'
import { mapActions, mapGetters } from 'vuex'

export default {
	name: 'HomeDashboard',

	components: {
		VClamp,
	},

	data() {
		return {
			path: process.env.VUE_APP_API_URL,

			// UI State
			snackbar: false,
			color: '',
			message: '',

			// Student Selection
			activeStudent: 0,

			// Loading States
			cartLoading: false,
			wishlistLoading: false,
			partIndex: null,

			// Filters
			searchQuery: '',
			selectedSubCategory: null, // Selected subcategory object
			selectedLearningArea: null, // Selected learning area object
			categoryFilteredParts: [], // Parts fetched by category filter
			learningAreaFilteredParts: [], // Parts fetched by learning area filter
			categoryFilterLoading: false,
			priceRange: [0, 100000],

			// View Options
			viewMode: 'grid',
			sortBy: 'name-asc',
			sortOptions: [
				{ text: 'Name (A-Z)', value: 'name-asc' },
				{ text: 'Name (Z-A)', value: 'name-desc' },
				{ text: 'Price (Low-High)', value: 'price-asc' },
				{ text: 'Price (High-Low)', value: 'price-desc' },
				{ text: 'Purchased First', value: 'purchased' },
				{ text: 'In Stock First', value: 'stock' },
			],

			// Mobile
			mobileFilterDrawer: false,

			// Category Icons Mapping
			categoryIcons: {
				'Books': 'mdi-book-open-page-variant',
				'Textbooks': 'mdi-book-education',
				'Stationery': 'mdi-pencil',
				'Uniforms': 'mdi-tshirt-crew',
				'Sports': 'mdi-soccer',
				'Art': 'mdi-palette',
				'Science': 'mdi-flask',
				'Electronics': 'mdi-laptop',
				'default': 'mdi-tag',
			},
		}
	},

	computed: {
		...mapGetters(['allAds', 'getProfile', 'allStudentLevels', 'studentLevelLoader', 'allCategories', 'allLearningAreas']),

		// Current student data
		currentStudent() {
			if (this.allStudentLevels.length === 0) return {}
			return this.allStudentLevels[this.activeStudent]?.student || {}
		},

		currentStudentLevel() {
			if (this.allStudentLevels.length === 0) return null
			return this.allStudentLevels[this.activeStudent]?.student_level || null
		},

		currentStudentParts() {
			if (this.allStudentLevels.length === 0) return []
			return this.allStudentLevels[this.activeStudent]?.parts || []
		},

		// Stats
		purchasedItemsCount() {
			return this.currentStudentParts.filter(p => p.purchased_quantity > 0).length
		},

		pendingItemsCount() {
			return this.currentStudentParts.filter(p => p.purchased_quantity === 0).length
		},

		// Price range - based on displayParts (which changes based on selected filter)
		minPrice() {
			const parts = this.displayParts
			if (!parts || parts.length === 0) return 0
			const prices = parts.map(p => p.price).filter(p => p !== undefined && p !== null)
			return prices.length > 0 ? Math.min(...prices) : 0
		},

		maxPrice() {
			const parts = this.displayParts
			if (!parts || parts.length === 0) return 100000
			const prices = parts.map(p => p.price).filter(p => p !== undefined && p !== null)
			return prices.length > 0 ? Math.max(...prices) : 100000
		},

		// Learning areas available for the selected student (extracted from their parts)
		studentLearningAreas() {
			const areaMap = new Map()

			// Extract learning areas from the current student's parts
			this.currentStudentParts.forEach(part => {
				if (part.learning_area_parts && part.learning_area_parts.length > 0) {
					part.learning_area_parts.forEach(lap => {
						const area = lap.learning_area
						if (area && area.id) {
							areaMap.set(area.id, area)
						}
					})
				}
			})

			return Array.from(areaMap.values()).sort((a, b) =>
				(a.name || '').localeCompare(b.name || '')
			)
		},

		// Check if any filters are active
		hasActiveFilters() {
			return !!this.searchQuery || !!this.selectedSubCategory || !!this.selectedLearningArea
		},

		// Get the parts to display (either filtered by category/learning area or all student parts)
		displayParts() {
			// If a subcategory is selected, use the fetched category filtered parts
			if (this.selectedSubCategory) {
				return this.categoryFilteredParts
			}
			// If a learning area is selected, use the fetched learning area filtered parts
			if (this.selectedLearningArea) {
				return this.learningAreaFilteredParts
			}
			// Otherwise show all student parts
			return this.currentStudentParts
		},

		// Filtered and sorted parts
		filteredParts() {
			let parts = [...this.displayParts]

			// Search filter
			if (this.searchQuery) {
				const query = this.searchQuery.toLowerCase()
				parts = parts.filter(p =>
					p.name.toLowerCase().includes(query) ||
					(p.brief && p.brief.toLowerCase().includes(query))
				)
			}

			// Price range filter
			parts = parts.filter(p =>
				p.price >= this.priceRange[0] && p.price <= this.priceRange[1]
			)

			// Sorting
			switch (this.sortBy) {
				case 'name-asc':
					parts.sort((a, b) => a.name.localeCompare(b.name))
					break
				case 'name-desc':
					parts.sort((a, b) => b.name.localeCompare(a.name))
					break
				case 'price-asc':
					parts.sort((a, b) => a.price - b.price)
					break
				case 'price-desc':
					parts.sort((a, b) => b.price - a.price)
					break
				case 'purchased':
					parts.sort((a, b) => b.purchased_quantity - a.purchased_quantity)
					break
				case 'stock':
					parts.sort((a, b) => b.quantity - a.quantity)
					break
			}

			return parts
		},
	},

	watch: {
		// Reset price range when student changes
		activeStudent() {
			this.$nextTick(() => {
				this.priceRange = [this.minPrice, this.maxPrice]
			})
		},
	},

	created() {
		window.scrollTo(0, 0)
		this.fetchAllStudentLevels()
		this.fetchAllAds()
		this.fetchAllCategories()
		this.fetchAllLearningAreas()
	},

	mounted() {
		// Initialize price range after data loads
		this.$nextTick(() => {
			if (this.currentStudentParts.length > 0) {
				this.priceRange = [this.minPrice, this.maxPrice]
			}
		})
	},

	methods: {
		...mapActions([
			'fetchAllAds',
			'fetchAllStudentLevels',
			'fetchCarts',
			'fetchWishlists',
			'fetchAllCategories',
			'fetchAllLearningAreas',
		]),

		// Student selection
		assignStudent(index) {
			this.activeStudent = index
			this.clearAllFilters()
		},

		// Select a subcategory and fetch products
		selectSubCategory(subCategory) {
			this.selectedSubCategory = subCategory
			this.selectedLearningArea = null // Clear learning area filter
			this.learningAreaFilteredParts = []
			this.fetchCategoryProducts(subCategory.slug)
		},

		// Clear category filter
		clearCategoryFilter() {
			this.selectedSubCategory = null
			this.categoryFilteredParts = []
		},

		// Fetch products by subcategory
		fetchCategoryProducts(subcategorySlug) {
			this.categoryFilterLoading = true

			apiCall({
				url: `/api/part?page=1&type=subcategory&subcategory=${subcategorySlug}`,
				method: 'GET',
			})
				.then((resp) => {
					// API returns paginated response with data array
					if (resp && resp.data) {
						this.categoryFilteredParts = resp.data
					} else if (Array.isArray(resp)) {
						this.categoryFilteredParts = resp
					} else {
						this.categoryFilteredParts = []
					}
					this.categoryFilterLoading = false
					// Update price range after data is loaded
					this.$nextTick(() => {
						this.priceRange = [this.minPrice, this.maxPrice]
					})
				})
				.catch((error) => {
					console.log('Category fetch error:', error)
					this.categoryFilteredParts = []
					this.categoryFilterLoading = false
					this.showNotification('Error loading category products', 'error')
				})
		},

		// Select a learning area and filter products for the current student
		selectLearningArea(learningArea) {
			this.selectedLearningArea = learningArea
			this.selectedSubCategory = null // Clear category filter
			this.categoryFilteredParts = []
			this.filterPartsbyLearningArea(learningArea)
		},

		// Clear learning area filter
		clearLearningAreaFilter() {
			this.selectedLearningArea = null
			this.learningAreaFilteredParts = []
			// Reset price range to student parts range
			this.$nextTick(() => {
				this.priceRange = [this.minPrice, this.maxPrice]
			})
		},

		// Filter current student's parts by learning area (client-side filtering)
		filterPartsbyLearningArea(learningArea) {
			this.categoryFilterLoading = true

			// Filter current student's parts by the selected learning area
			const filteredParts = this.currentStudentParts.filter(part => {
				if (!part.learning_area_parts || part.learning_area_parts.length === 0) {
					return false
				}
				return part.learning_area_parts.some(lap =>
					lap.learning_area && lap.learning_area.id === learningArea.id
				)
			})

			this.learningAreaFilteredParts = filteredParts
			this.categoryFilterLoading = false

			// Update price range after filtering
			this.$nextTick(() => {
				this.priceRange = [this.minPrice, this.maxPrice]
			})
		},

		// Get student initials for avatar
		getInitials(student) {
			const first = student.first_name?.charAt(0) || ''
			const last = student.last_name?.charAt(0) || ''
			return (first + last).toUpperCase()
		},

		// Get category icon
		getCategoryIcon(categoryName) {
			for (const [key, icon] of Object.entries(this.categoryIcons)) {
				if (categoryName.toLowerCase().includes(key.toLowerCase())) {
					return icon
				}
			}
			return this.categoryIcons.default
		},

		// Get part categories
		getPartCategories(part) {
			if (!part.category_parts) return []
			return part.category_parts
				.map(cp => cp.category?.name || cp.sub_category?.name)
				.filter(Boolean)
		},

		// Get part learning area
		getPartLearningArea(part) {
			if (!part.learning_area_parts || part.learning_area_parts.length === 0) return null
			return part.learning_area_parts[0]?.learning_area?.name || null
		},

		// Format price
		formatPrice(price) {
			return Number(price).toLocaleString()
		},

		// Clear all filters
		clearAllFilters() {
			this.searchQuery = ''
			this.selectedSubCategory = null
			this.selectedLearningArea = null
			this.categoryFilteredParts = []
			this.learningAreaFilteredParts = []
			this.priceRange = [this.minPrice, this.maxPrice]
		},

		// Toggle wishlist
		toggleWishlist(item) {
			if (item.favourite) {
				this.removeFromWishlist(item)
			} else {
				this.addToWishlist(item)
			}
		},

		// Add to cart
		addToCart(item) {
			this.cartLoading = true
			this.partIndex = item.id

			apiCall({
				url: '/api/cart',
				data: item,
				method: 'POST',
			})
				.then((resp) => {
					if (resp.status === 200) {
						this.showNotification(resp.message, 'success')
						this.fetchAllStudentLevels()
						this.fetchCarts(1)
					} else if (resp.status === 409) {
						this.showNotification(resp.message, 'error')
					}
				})
				.catch(() => {
					this.showNotification('An error occurred while adding to cart', 'error')
				})
				.finally(() => {
					this.cartLoading = false
					this.partIndex = null
				})
		},

		// Add to wishlist
		addToWishlist(item) {
			this.wishlistLoading = true
			this.partIndex = item.id

			apiCall({
				url: '/api/wishlist',
				data: item,
				method: 'POST',
			})
				.then((resp) => {
					if (resp.status === 200) {
						this.showNotification(resp.message, 'success')
						this.fetchAllStudentLevels()
						this.fetchWishlists(1)
					} else if (resp.status === 409) {
						this.showNotification(resp.message, 'error')
					}
				})
				.catch(() => {
					this.showNotification('An error occurred', 'error')
				})
				.finally(() => {
					this.wishlistLoading = false
					this.partIndex = null
				})
		},

		// Remove from wishlist
		removeFromWishlist(item) {
			this.wishlistLoading = true
			this.partIndex = item.id

			apiCall({
				url: '/api/wishlist/' + item.id,
				method: 'DELETE',
			})
				.then(() => {
					this.showNotification('Item removed from wishlist', 'success')
					this.fetchAllStudentLevels()
					this.fetchWishlists(1)
				})
				.catch(() => {
					this.showNotification('An error occurred', 'error')
				})
				.finally(() => {
					this.wishlistLoading = false
					this.partIndex = null
				})
		},

		// Show notification
		showNotification(message, color) {
			this.message = message
			this.color = color
			this.snackbar = true
		},
	},
}
</script>
